import React, { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Feather from 'react-native-vector-icons/Feather';
import { MB_Button } from '@/components/general/button';
import { MB_Modal } from '@/components/general/modal';
import { MB_TextInput } from '@/components/general/text-input';
import { mbTextStyles } from '@/utils/general/style-utils';
import { imagePoolApiCalls } from '../../../../apiCalls/imagePoolApiCalls';
import { videoPoolApiCalls } from '../../../../apiCalls/videoPoolApiCalls';
import { adminApiCalls } from '../../../../apiCalls/adminApiCalls';
import { COLORS } from '../../../../constants/colors';
import { textStyles } from '../../../../constants/textStyles';
import { useGetFolders } from '../../../../hooks/adminHooks';
import { ADMIN_QUERY_KEYS, IMAGE_VIDEO_POOL_QUERY_KEYS } from '../../../../constants/constants';
import { Folder, FOLDER_TYPE, PoolImage, PoolVideo, RESOURCE_POOL_TYPE } from '../../../../typesAndInterfaces/typesAndInterfaces';

const MOVE_CONCURRENCY = 3;

/** Sentinel for "move to root": the edit endpoints interpret '' as unset-folder. */
const ROOT_TARGET = '';

interface MoveToFolderModalProps {
  isVisible: boolean;
  onDismiss: (didMove: boolean) => void;
  resourcePoolType: RESOURCE_POOL_TYPE;
  items: (PoolImage | PoolVideo)[];
  /** The folder currently open, so it can be excluded from the target list. */
  currentFolderId?: string;
}

/**
 * Target picker for single/multi move. Moves run per item through the existing
 * edit endpoints (name unchanged, folderId set) with limited concurrency —
 * the same worker-pool pattern as bulk upload.
 */
const MoveToFolderModal = ({ isVisible, onDismiss, resourcePoolType, items, currentFolderId }: MoveToFolderModalProps) => {
  const isImagePool = resourcePoolType === RESOURCE_POOL_TYPE.Image;
  const { data: folders } = useGetFolders({ type: isImagePool ? FOLDER_TYPE.image : FOLDER_TYPE.video });

  const [selectedTarget, setSelectedTarget] = useState<string | undefined>(undefined);
  const [newFolderName, setNewFolderName] = useState('');
  const [isMoving, setIsMoving] = useState(false);
  const [errorText, setErrorText] = useState<string | undefined>(undefined);
  const queryClient = useQueryClient();

  const targets = useMemo(() => {
    const folderTargets = (folders ?? []).filter(folder => folder._id !== currentFolderId).map(folder => ({ id: folder._id, label: folder.name }));
    return currentFolderId ? [{ id: ROOT_TARGET, label: 'Root (no folder)' }, ...folderTargets] : folderTargets;
  }, [currentFolderId, folders]);

  const isNewFolderMode = selectedTarget === 'new-folder';

  const onMovePress = useCallback(async () => {
    setErrorText(undefined);
    setIsMoving(true);
    try {
      let targetFolderId = selectedTarget;

      // "Create folder" affordance at move time: create it, then move into it.
      if (isNewFolderMode) {
        const trimmed = newFolderName.trim();
        if (!trimmed) {
          setErrorText('Folder name is required.');
          return;
        }
        const newFolder: Folder = await adminApiCalls.createFolder(trimmed, isImagePool ? FOLDER_TYPE.image : FOLDER_TYPE.video);
        targetFolderId = newFolder._id;
      }

      if (targetFolderId === undefined) {
        setErrorText('Pick a destination.');
        return;
      }

      const queue = [...items];
      const failedNames: string[] = [];
      const worker = async () => {
        while (queue.length > 0) {
          const item = queue.shift();
          if (!item) {
            break;
          }
          try {
            if (isImagePool) {
              await imagePoolApiCalls.editImage(item._id, item.name, targetFolderId);
            } else {
              await videoPoolApiCalls.editVideo(item._id, item.name, targetFolderId);
            }
          } catch {
            failedNames.push(item.name);
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(MOVE_CONCURRENCY, items.length) }, worker));

      // Moves go through the raw api calls (not the react-query mutations), so
      // refresh the pool list and folders explicitly.
      queryClient.invalidateQueries([isImagePool ? IMAGE_VIDEO_POOL_QUERY_KEYS.getImagePoolImages : IMAGE_VIDEO_POOL_QUERY_KEYS.getVideoPoolVideos]);
      queryClient.invalidateQueries([ADMIN_QUERY_KEYS.getFolders]);

      if (failedNames.length > 0) {
        setErrorText(`Failed to move: ${failedNames.join(', ')}. The rest were moved.`);
        return;
      }

      onDismiss(true);
    } catch {
      setErrorText('Could not create the folder. It may already exist.');
    } finally {
      setIsMoving(false);
    }
  }, [isImagePool, isNewFolderMode, items, newFolderName, onDismiss, queryClient, selectedTarget]);

  return (
    <MB_Modal isVisible={isVisible} onDismiss={() => !isMoving && onDismiss(false)} title={`Move ${items.length} ${isImagePool ? 'image' : 'video'}${items.length > 1 ? 's' : ''} to…`} childrenWrapperStyle={styles.modal}>
      <View style={styles.body}>
        <ScrollView style={styles.targetList}>
          {targets.map(target => (
            <TouchableOpacity key={target.id || 'root'} style={[styles.targetRow, selectedTarget === target.id && styles.targetRowSelected]} onPress={() => setSelectedTarget(target.id)} testID={`move-target-${target.label}`}>
              <Feather name={target.id === ROOT_TARGET ? 'home' : 'folder'} size={16} color={COLORS.textLightPurple} />
              <Text style={styles.targetLabel} numberOfLines={1}>
                {target.label}
              </Text>
              {selectedTarget === target.id && <Feather name="check" size={16} color={COLORS.buttonPurple} />}
            </TouchableOpacity>
          ))}
          <TouchableOpacity style={[styles.targetRow, isNewFolderMode && styles.targetRowSelected]} onPress={() => setSelectedTarget('new-folder')} testID="move-target-new-folder">
            <Feather name="folder-plus" size={16} color={COLORS.textLightPurple} />
            <Text style={styles.targetLabel}>New folder…</Text>
            {isNewFolderMode && <Feather name="check" size={16} color={COLORS.buttonPurple} />}
          </TouchableOpacity>
        </ScrollView>

        {isNewFolderMode && <MB_TextInput value={newFolderName} onChangeText={setNewFolderName} placeholder="New folder name" maxLength={30} style={styles.newFolderInput} />}

        {errorText !== undefined && <Text style={styles.errorText}>{errorText}</Text>}

        <View style={styles.buttons}>
          <MB_Button title="Cancel" disabled={isMoving} style={styles.cancelButton} textStyle={{ color: COLORS.buttonPurple }} onPress={() => onDismiss(false)} />
          <MB_Button title={isMoving ? 'Moving…' : 'Move'} disabled={isMoving} style={styles.moveButton} onPress={onMovePress} />
        </View>
      </View>
    </MB_Modal>
  );
};

export { MoveToFolderModal };

const styles = StyleSheet.create({
  modal: {
    width: 420,
    padding: 24,
  },
  body: {
    width: '100%',
  },
  targetList: {
    maxHeight: 280,
    width: '100%',
  },
  targetRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginBottom: 4,
  },
  targetRowSelected: {
    backgroundColor: COLORS.backgroundPurple,
  },
  targetLabel: mbTextStyles([
    textStyles.smallText,
    {
      flex: 1,
      textAlign: 'left',
      marginStart: 10,
    },
  ]),
  newFolderInput: {
    width: '100%',
    marginTop: 8,
  },
  errorText: mbTextStyles([
    textStyles.smallText,
    {
      color: COLORS.errorColor,
      textAlign: 'left',
      marginTop: 8,
    },
  ]),
  buttons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 20,
  },
  cancelButton: {
    backgroundColor: COLORS.white,
    marginRight: 12,
    paddingHorizontal: 20,
  },
  moveButton: {
    backgroundColor: COLORS.buttonPurple,
    paddingHorizontal: 24,
  },
});
